import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { OperacionesClienteFinal, ResultadoIdentificacion, TokenCliente } from '../api/ops/clienteFinal';
import type { Catalogo, OperacionesMisReservas, PresupuestoPublico, ZonaCatalogo } from '../api/ops/misReservas';
import { claveIdempotencia } from '../api/comun';
import { ErrorApi } from '../api/problema';
import { Campo } from '../componentes/Campo';
import { Casilla } from '../componentes/Casilla';
import { Desplegable } from '../componentes/Desplegable';
import { Fecha } from '../componentes/Fecha';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { dinero } from '../logica/dinero';
import { Contador } from '../componentes/Contador';
import { diaLargo, horaEn, hoyEn, sumarDias } from '../pantallas/reservas/fechas';
import { Identificarse } from './Identificarse';
import { AvisoPc, Baldosa, BotonPc, EnlaceLegal, LegalCaptcha } from './Pagina';
import { cierreDeEntrada, esBaja, leerEntrada, type Cierre } from './entradaCerrada';
import { marcaDelWidget, tintaSobre, type Tema } from './marca';
import { horasEstancia } from './logica';
import './reserva.css';

/**
 * 8.11 Reserva pública y 8.9 estancia (cliente), en la web y en el widget (G647, G672, ADR-0182): con la marca del
 * negocio (G614, G618) y sin el fondo negro ni el rojo de Reservaria; dentro del widget, sin cinta.
 *
 * - Web: `/reservar/{slug}` → `getPublicEntry` (G885: aunque el negocio no tenga web publicada; marca `brand.primary_color`,
 *   tema claro, privacidad y la clave pública de la web `api_key`).
 * - Widget: `/reservar?clave=pk_…&marco=widget[&tema=claro|oscuro|auto][&acento=RRGGBB]` → `getPublicWidget` (`theme` y
 *   `accent_color`; `tema` y `acento` de la dirección mandan, como 13.8), con «Privacidad de [negocio]» en el pie
 *   (`privacy_url`, G869).
 * - Con `&evento=evn_…` (widget `entradas.reservados`, G905): los reservados de ese evento. El catálogo con `event_id` da sus
 *   ofertas; solo se ofrecen sus zonas y las franjas de ese evento (`event_offer_id` al buscar) y la reserva lleva el evento.
 *
 * Pasos: elegir (zona o recurso si el negocio deja, G405; personas; día con la antelación y el horizonte de
 * `booking_window`; hora en botones, `searchReservationSlots`; en estancias, entrada y noches; evento si hay varios,
 * G18, G21) → identificarse (2.9, `identifyCustomerInBusiness`) → revisar (`quoteReservation`: lo que se paga y la política
 * con su `terms_text`; nota; «Alergias o intolerancias» con sus dos casillas, G192) → reservar (`createOnlineReservation` con
 * antibot invisible, `expected_total` e `Idempotency-Key`; si alguien se adelanta, `409`, se vuelve a elegir sin cobrar
 * nada; si el precio cambió, se enseña el nuevo) → pagar (4.9, 30 min, G39) o hecha.
 */
export interface PropsReservaPublica {
    ops: OperacionesClienteFinal;
    reservas: OperacionesMisReservas;
    /** `slug` de la web del negocio; sin él, el widget con `clave`. */
    slug: string | null;
    busqueda: URLSearchParams;
    captcha: (accion: string) => Promise<string | null>;
    onToken: (token: TokenCliente) => void;
    /** Ya tiene token de cliente (de «Mis datos» o de otra reserva): se identifica en el negocio sin pedir otro código. */
    conToken?: boolean;
    navegar: (ruta: string) => void;
    /** Abre la página de pago (por defecto, el navegador). */
    abrir?: (url: string) => void;
    ahora?: () => number;
}

interface Negocio {
    id: string;
    nombre: string;
    clave: string;
    privacidad: string;
    zona: string;
    marca: string;
    tema: Tema;
    widget: boolean;
    /** Cerrada (`getPublicEntry`, G885/G932; o el widget no activo, como `pausado`): la página no vende. */
    cierre: Cierre | null;
}

type Paso = 'elegir' | 'identificarse' | 'revisar' | 'hecha';

interface Eleccion {
    zona: string;
    recurso: string;
    personas: number;
    dia: string;
    hora: string | null;
    turno: string | null;
    noches: number;
    evento: string | null;
}

const SIN_CLAVE = 'sin-clave';

/** La oferta de reservados de un evento para una zona (G905). */
function ofertaDeZona(c: Catalogo, zona: string, evento: string) {
    return c.event_offers.find((o) => o.event?.event_id === evento && o.target?.zone_id === zona);
}
const CONSENTIMIENTO = 'Te damos este dato para que preparen mi comida';
const GUARDAR = 'Guárdalo para mis próximas reservas';

/** La página cerrada (G885, G932): baldosa, chip, qué pasa y «Ver mis reservas»; no vende. */
function TarjetaCerrada({ icono, chip, titulo, texto, onMisReservas }: { icono: NombreIcono; chip: string; titulo: string; texto: string; onMisReservas: () => void }) {
    return (
        <section className="rv-pc-card">
            <div className="rv-pc-baldosa-fila"><Baldosa icono={icono} fondo="var(--rv-paper)" color="var(--rv-ink)" /><Chip estado="neutral">{chip}</Chip></div>
            <div className="rv-pc-card__cab"><h1>{titulo}</h1><span>{texto}</span></div>
            <BotonPc texto="Ver mis reservas" icono="calendar-check" onClick={onMisReservas} />
        </section>
    );
}

function Marco({ negocio, linea, children }: { negocio: Negocio | null; linea: string; children: ReactNode }) {
    const marca = negocio?.marca ?? '#111111';
    const estilo = { '--rv-red': marca, '--rv-red-hover': marca, '--rv-rp-tinta': tintaSobre(marca) } as CSSProperties;
    return (
        <div className={`rv-pc rv-rp rv-rp--${negocio?.tema ?? 'claro'}${negocio?.widget === true ? ' rv-rp--widget' : ''}`} style={estilo} data-pagina-cliente="">
            <div className="rv-pc__col">
                {negocio !== null && !negocio.widget && (
                    <header className="rv-pc__cab rv-rp__cab">
                        <span aria-hidden="true" className="rv-pc__avatar rv-rp__avatar">{negocio.nombre.slice(0, 1).toUpperCase()}</span>
                        <span className="rv-pc__marca"><strong>{negocio.nombre}</strong><span>{linea}</span></span>
                    </header>
                )}
                <main className="rv-pc__main">{children}</main>
                {negocio !== null && !negocio.widget && (
                    <footer className="rv-rp__pie">
                        <EnlaceLegal url={negocio.privacidad}>{`Privacidad de ${negocio.nombre}`}</EnlaceLegal>
                        <span aria-hidden="true">·</span>
                        <span>Ofrecido por Reservaria</span>
                    </footer>
                )}
                {/* G869: dentro del widget, solo «Privacidad de [negocio]» (`PublicWidget.privacy_url`). */}
                {negocio !== null && negocio.widget && negocio.privacidad !== '' && (
                    <footer className="rv-rp__pie">
                        <EnlaceLegal url={negocio.privacidad}>{`Privacidad de ${negocio.nombre}`}</EnlaceLegal>
                    </footer>
                )}
            </div>
        </div>
    );
}

export function ReservaPublica({ ops, reservas, slug, busqueda, captcha, onToken, conToken = false, navegar, abrir = (url) => { window.location.assign(url); }, ahora = Date.now }: PropsReservaPublica) {
    const [negocio, setNegocio] = useState<Negocio | null>(null);
    const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
    const [error, setError] = useState<'no-existe' | 'baja' | 'error' | null>(null);
    const [intento, setIntento] = useState(0);
    const [paso, setPaso] = useState<Paso>('elegir');
    const [e, setE] = useState<Eleccion>({ zona: busqueda.get('zona') ?? '', recurso: '', personas: 2, dia: '', hora: null, turno: null, noches: 1, evento: null });
    const [huecos, setHuecos] = useState<{ clave: string; datos: Awaited<ReturnType<OperacionesMisReservas['searchReservationSlotsPublic']>> | null } | null>(null);
    const [cliente, setCliente] = useState<ResultadoIdentificacion | null>(null);
    const [presupuesto, setPresupuesto] = useState<PresupuestoPublico | null>(null);
    const [nota, setNota] = useState('');
    const [alergias, setAlergias] = useState({ texto: '', consiento: false, guardar: false });
    const [aviso, setAviso] = useState<{ texto: string; tipo: 'error' | 'espera' | 'info' } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [hecha, setHecha] = useState<{ referencia: string; pago: { url: string; hasta: string | null } | null } | null>(null);
    const clave = useRef(claveIdempotencia());
    const claveWidget = busqueda.get('clave');
    // G905: el evento fijo del widget `entradas.reservados` (`&evento=`).
    const eventoFijo = /^evn_[0-9A-HJKMNP-TV-Z]{26}$/.test(busqueda.get('evento') ?? '') ? busqueda.get('evento') : null;

    // El negocio: su web por el `slug` o su widget por la clave.
    useEffect(() => {
        let vivo = true;
        const cargar = async (): Promise<Negocio> => {
            if (slug !== null) {
                // G885: la entrada pública, aunque el negocio no tenga la web publicada.
                const w = await leerEntrada(() => reservas.getPublicEntry(slug));
                return { id: w.business.business_id, nombre: w.business.name, clave: w.api_key, privacidad: w.privacy_url, zona: w.business.timezone, marca: w.brand?.primary_color ?? '#111111', tema: 'claro', widget: false, cierre: cierreDeEntrada(w) };
            }
            if (claveWidget === null || claveWidget === '') {
                throw new Error(SIN_CLAVE);
            }
            const w = await reservas.getPublicWidget(claveWidget);
            const m = marcaDelWidget(w, busqueda);
            return { id: w.business_id, nombre: w.business_name ?? '', clave: claveWidget, privacidad: w.privacy_url ?? '', zona: Intl.DateTimeFormat().resolvedOptions().timeZone, marca: m.acento, tema: m.tema, widget: true, cierre: w.status !== 'active' ? { motivo: 'pausado', vuelve: null } : null };
        };
        cargar()
            .then(async (n) => {
                if (n.cierre !== null) {
                    // Cerrada: no vende, no hace falta el catálogo.
                    if (vivo) {
                        setNegocio(n);
                        setError(null);
                    }
                    return;
                }
                const leido = await reservas.getReservationCatalog(n.id, n.clave, eventoFijo ?? undefined);
                // G905: con el evento, solo las zonas de sus ofertas.
                const c = eventoFijo === null ? leido : { ...leido, zones: leido.zones.filter((z) => ofertaDeZona(leido, z.zone_id, eventoFijo) !== undefined) };
                if (eventoFijo !== null && c.zones.length === 0) {
                    throw new Error(SIN_CLAVE);
                }
                if (vivo) {
                    setNegocio({ ...n, zona: c.timezone ?? n.zona });
                    setCatalogo(c);
                    setError(null);
                    setE((x) => {
                        const z = c.zones.find((y) => y.zone_id === x.zona) ?? c.zones[0];
                        return { ...x, dia: x.dia === '' ? hoyEn(c.timezone ?? n.zona, new Date(ahora())) : x.dia, zona: z?.zone_id ?? '', noches: Math.max(z?.min_nights ?? 1, 1), personas: z === undefined ? x.personas : Math.min(Math.max(x.personas, z.min_party), z.max_party) };
                    });
                }
            })
            .catch((x: unknown) => { if (vivo) { setError(esBaja(x) ? 'baja' : (x instanceof ErrorApi && (x.estado === 404 || x.estado === 410 || x.estado === 401)) || (x instanceof Error && x.message === SIN_CLAVE) ? 'no-existe' : 'error'); } });
        return () => { vivo = false; };
        // `busqueda` y `ahora` solo se leen al cargar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, reservas, slug, claveWidget, intento]);

    const zona: ZonaCatalogo | undefined = catalogo?.zones.find((z) => z.zone_id === e.zona);
    const estancia = zona?.modality === 'stay';
    const destino = e.recurso !== '' ? { resource_id: e.recurso } : { zone_id: e.zona };
    const claveHuecos = `${e.zona}|${e.recurso}|${String(e.personas)}|${e.dia}|${estancia ? String(e.noches) : ''}`;

    useEffect(() => {
        if (negocio === null || zona === undefined || e.dia === '') {
            return;
        }
        let vivo = true;
        const oferta = eventoFijo === null || catalogo === null ? undefined : ofertaDeZona(catalogo, e.zona, eventoFijo);
        reservas.searchReservationSlotsPublic(negocio.id, negocio.clave, { target: destino, party_size: e.personas, mode: 'list', from_date: e.dia, to_date: e.dia, ...(estancia ? { nights: e.noches } : {}), ...(oferta?.offer_id === undefined ? {} : { event_offer_id: oferta.offer_id }) })
            .then((d) => { if (vivo) { setHuecos({ clave: claveHuecos, datos: d }); } })
            .catch(() => { if (vivo) { setHuecos({ clave: claveHuecos, datos: null }); } });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservas, negocio, claveHuecos]);

    if (error === 'baja') {
        // G932: con `410` no llega la marca: colores de Reservaria y sin el nombre del negocio.
        return (
            <Marco negocio={null} linea="">
                <TarjetaCerrada icono="unlink" chip="No disponible" titulo="Esta dirección ya no reserva" texto="Este negocio ya no acepta reservas por internet con esta dirección. Si tienes una reserva, la encuentras en «Mis reservas»." onMisReservas={() => { navegar('/mis-reservas'); }} />
            </Marco>
        );
    }
    if (error !== null) {
        return (
            <Marco negocio={negocio} linea="Reservar">
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>{error === 'no-existe' ? 'No encontramos esta página' : 'No hemos podido cargar la reserva'}</h1><span>{error === 'no-existe' ? 'Puede que el enlace ya no valga.' : 'Comprueba la conexión y prueba de nuevo.'}</span></div>
                    {error === 'error' && <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setIntento((i) => i + 1); }} />}
                </section>
            </Marco>
        );
    }
    if (negocio?.cierre != null) {
        const { cierre } = negocio;
        const misReservas = () => { navegar('/mis-reservas'); };
        const verla = 'Si ya tienes una reserva, puedes verla o cancelarla.';
        return (
            <Marco negocio={negocio} linea="Reservar">
                {cierre.motivo === 'temporada' ? (
                    <TarjetaCerrada icono="calendar-x" chip="Cerrado por temporada" titulo="Cerrado por temporada" texto={cierre.vuelve === null ? verla : `${negocio.nombre} vuelve a abrir el ${diaLargo(cierre.vuelve)}. Desde entonces podrás reservar aquí. ${verla}`} onMisReservas={misReservas} />
                ) : cierre.motivo === 'retirada' ? (
                    <TarjetaCerrada icono="calendar-x" chip="Sin reservas online" titulo="Esta página no está disponible" texto={`Ahora no se puede reservar con ${negocio.nombre} por internet. Ponte en contacto con ellos para reservar. ${verla}`} onMisReservas={misReservas} />
                ) : (
                    <TarjetaCerrada icono="calendar-x" chip="Sin reservas online" titulo="Ahora no se puede reservar online" texto={`${negocio.nombre} no está aceptando reservas por internet en este momento. Ponte en contacto con ellos para reservar. ${verla}`} onMisReservas={misReservas} />
                )}
            </Marco>
        );
    }
    if (negocio === null || catalogo === null) {
        return (
            <Marco negocio={null} linea="">
                <section className="rv-pc-card" aria-busy="true"><div className="rv-pc-esqueleto" style={{ height: 56 }} /><div className="rv-pc-esqueleto" style={{ height: 160 }} /></section>
            </Marco>
        );
    }

    const unidad = (zona?.unit ?? '').trim() || 'sitio';
    const linea = `Reservar ${unidad}`;

    const leidos = huecos?.clave === claveHuecos ? huecos.datos : undefined;
    // G905: con el evento fijo, solo las franjas de ese evento.
    const actuales = leidos === undefined || leidos === null || eventoFijo === null ? leidos : { ...leidos, slots: leidos.slots.filter((s) => (s.events ?? []).some((x) => x.event_id === eventoFijo)) };
    const ventana = zona?.booking_window ?? null;
    const hoy = hoyEn(negocio.zona, new Date(ahora()));
    const maxDia = ventana === null ? undefined : sumarDias(hoy, ventana.max_advance_days);
    const hueco = actuales?.slots.find((s) => (estancia ? s.local_date === e.dia : e.turno !== null ? s.shift_id === e.turno : s.local_time === e.hora));
    const eventos = hueco?.events ?? [];
    const peticion = () => ({
        target: destino,
        party_size: e.personas,
        when: estancia ? { check_in_date: e.dia, check_out_date: sumarDias(e.dia, e.noches) } : e.turno !== null ? { local_date: e.dia, shift_id: e.turno } : { local_date: e.dia, local_time: e.hora ?? '' },
        ...((eventoFijo ?? e.evento) === null ? {} : { event_id: eventoFijo ?? e.evento ?? '' }),
    });
    const listoParaSeguir = hueco !== undefined && (eventoFijo !== null || eventos.length <= 1 || e.evento !== null);

    const revisar = () => {
        setOcupado(true);
        setAviso(null);
        reservas.quoteReservationPublic(negocio.id, negocio.clave, peticion())
            .then((p) => { setPresupuesto(p); setPaso('revisar'); clave.current = claveIdempotencia(); })
            .catch(() => { setAviso({ tipo: 'error', texto: 'No hemos podido calcular el precio. Prueba de nuevo.' }); })
            .finally(() => { setOcupado(false); });
    };

    const seguir = () => {
        if (cliente !== null) {
            revisar();
            return;
        }
        if (!conToken) {
            setPaso('identificarse');
            return;
        }
        setOcupado(true);
        ops.identifyCustomerInBusiness(negocio.id, negocio.clave, { source: negocio.widget ? 'widget' : 'web' })
            .then((r) => { setCliente(r); revisar(); })
            .catch(() => { setOcupado(false); setPaso('identificarse'); });
    };

    const reservar = async () => {
        if (presupuesto === null) {
            return;
        }
        if (alergias.texto.trim() !== '' && !alergias.consiento) {
            setAviso({ tipo: 'error', texto: 'Para guardar tus alergias o intolerancias, marca la primera casilla. Si no, déjalo en blanco.' });
            return;
        }
        setOcupado(true);
        setAviso(null);
        const token = await captcha('reservar');
        reservas.createOnlineReservation(negocio.id, negocio.clave, {
            ...peticion(),
            expected_total: presupuesto.total,
            ...(nota.trim() === '' ? {} : { customer_note: nota.trim() }),
            ...(alergias.texto.trim() === '' ? {} : { health_info: { text: alergias.texto.trim(), consent: true, keep: alergias.guardar } }),
            ...(presupuesto.payment.policy_version_id === undefined ? {} : { accepted_terms_version: presupuesto.payment.policy_version_id }),
        }, token, clave.current)
            .then((r) => {
                setHecha({ referencia: r.reservation.reference, pago: r.next_action.kind === 'pay' && r.next_action.payment_url !== undefined ? { url: r.next_action.payment_url, hasta: r.next_action.expires_at ?? null } : null });
                setPaso('hecha');
            })
            .catch((x: unknown) => {
                if (x instanceof ErrorApi && x.estado === 409 && x.codigo === 'conflict') {
                    // El precio ha cambiado: el nuevo presupuesto, sin cobrar nada.
                    reservas.quoteReservationPublic(negocio.id, negocio.clave, peticion()).then(setPresupuesto).catch(() => undefined);
                    setAviso({ tipo: 'espera', texto: 'El precio ha cambiado mientras reservabas. Revisa el nuevo antes de seguir.' });
                } else if (x instanceof ErrorApi && x.estado === 409) {
                    setPaso('elegir');
                    setE((y) => ({ ...y, hora: null, turno: null, evento: null }));
                    setAviso({ tipo: 'error', texto: 'Alguien se ha adelantado y esa hora ya no está libre. Elige otra: no te hemos cobrado nada.' });
                } else if (x instanceof ErrorApi && x.codigo === 'captcha_required') {
                    setAviso({ tipo: 'error', texto: 'No hemos podido comprobar que no eres un robot. Prueba otra vez: no se ha borrado nada.' });
                } else {
                    setAviso({ tipo: 'error', texto: 'No hemos podido hacer la reserva. Prueba de nuevo: no se ha borrado nada.' });
                }
            })
            .finally(() => { setOcupado(false); });
    };

    if (paso === 'hecha' && hecha !== null) {
        return (
            <Marco negocio={negocio} linea={linea}>
                <section className="rv-pc-card" aria-labelledby="rv-rp-hecha">
                    <div className="rv-pc-card__cab">
                        <h1 id="rv-rp-hecha">{hecha.pago === null ? '¡Reserva hecha!' : 'Te guardamos el sitio'}</h1>
                        <span>{hecha.pago === null ? `Localizador ${hecha.referencia}. Te mandamos la confirmación y tu pase.` : `Paga ahora para confirmarla${hecha.pago.hasta === null ? '' : ` antes de las ${horaEn(hecha.pago.hasta, negocio.zona)}`}. Si no, el sitio se libera solo y no se cobra nada.`}</span>
                    </div>
                    {hecha.pago !== null && <BotonPc texto="Pagar ahora" icono="credit-card" onClick={() => { if (hecha.pago !== null) { abrir(hecha.pago.url); } }} />}
                    <BotonPc texto="Mis reservas" icono="calendar-check" tipo="sec" onClick={() => { navegar('/mis-reservas'); }} />
                </section>
            </Marco>
        );
    }

    if (paso === 'identificarse') {
        return (
            <Marco negocio={negocio} linea={linea}>
                <Identificarse
                    ops={ops}
                    contexto={{ tipo: 'negocio', negocio: { id: negocio.id, nombre: negocio.nombre, privacidad: negocio.privacidad, clavePublica: negocio.clave, origen: negocio.widget ? 'widget' : 'web' } }}
                    captcha={captcha}
                    onToken={onToken}
                    onIdentificadoEnNegocio={(r) => { setCliente(r); revisar(); }}
                    onNoSoyYo={() => { setCliente(null); setPaso('elegir'); }}
                    ahora={ahora}
                    tema={negocio.tema}
                />
                {aviso !== null && <AvisoPc texto={aviso.texto} tipo={aviso.tipo} />}
            </Marco>
        );
    }

    if (paso === 'revisar' && presupuesto !== null) {
        const cuando = estancia ? `${diaLargo(e.dia)} • ${e.noches === 1 ? '1 noche' : `${String(e.noches)} noches`}` : `${diaLargo(e.dia)} • ${e.hora ?? zona?.shifts?.find((t) => t.shift_id === e.turno)?.name ?? ''}`;
        const pago = presupuesto.payment;
        return (
            <Marco negocio={negocio} linea={linea}>
                <section className="rv-pc-card" aria-labelledby="rv-rp-revisa">
                    <div className="rv-pc-card__cab"><small className="rv-rp-eyebrow">Revisa tu reserva</small><h1 id="rv-rp-revisa">{`${unidad.slice(0, 1).toUpperCase()}${unidad.slice(1)} para ${String(e.personas)}`}</h1></div>
                    <ul className="rv-rp-datos">
                        <li><Icono nombre="calendar-days" /><span>{cuando}</span></li>
                        <li><Icono nombre="map-pin" /><span>{zona?.public_name}</span></li>
                        <li><Icono nombre="users" /><span>{`${String(e.personas)} personas`}</span></li>
                        {presupuesto.event != null && <li><Icono nombre="party-popper" /><span>{presupuesto.event.public_name}</span></li>}
                    </ul>
                    <button type="button" className="rv-rp-cambiar" onClick={() => { setPaso('elegir'); }}><Icono nombre="pencil" /><span>Cambiar algo</span></button>
                    {cliente !== null && <p className="rv-rp-nota">Te mandamos la confirmación y tu pase por tu canal de contacto.</p>}
                    <div className="rv-rp-pago">
                        <Icono nombre="scale" />
                        <span>
                            <strong>{pago.kind === 'none' ? `Total: ${dinero(presupuesto.total)}. No pagas nada ahora.` : pago.kind === 'guarantee' ? `Dejas una tarjeta de garantía${pago.guarantee_amount === undefined ? '' : ` de hasta ${dinero(pago.guarantee_amount)}`}` : `Para reservar pagas ${dinero(pago.amount_now ?? presupuesto.total)}`}</strong>
                            {pago.terms_text !== undefined && <small>{pago.terms_text}</small>}
                        </span>
                    </div>
                    {presupuesto.free_cancellation_until != null && <p className="rv-rp-nota"><Icono nombre="calendar-check" />{` Cancelación gratis hasta el ${diaLargo(hoyEn(negocio.zona, new Date(presupuesto.free_cancellation_until)))}, a las ${horaEn(presupuesto.free_cancellation_until, negocio.zona)}.`}</p>}
                    <Campo etiqueta="¿Algo que debamos saber? (opcional)" valor={nota} onChange={setNota} max={500} filas={3} tema={negocio.tema} />
                    <fieldset className="rv-rp-alergias">
                        <legend>Alergias o intolerancias (opcional)</legend>
                        <Campo etiqueta="Alergias o intolerancias" valor={alergias.texto} onChange={(v) => { setAlergias({ ...alergias, texto: v }); }} max={300} tema={negocio.tema} />
                        <Casilla marcada={alergias.consiento} onCambio={(v) => { setAlergias({ ...alergias, consiento: v }); }}>{CONSENTIMIENTO}</Casilla>
                        <Casilla marcada={alergias.guardar} onCambio={(v) => { setAlergias({ ...alergias, guardar: v }); }}>{GUARDAR}</Casilla>
                    </fieldset>
                    {aviso !== null && <AvisoPc texto={aviso.texto} tipo={aviso.tipo} />}
                    <BotonPc texto={pago.kind === 'none' ? 'Reservar' : pago.kind === 'guarantee' ? 'Reservar y dejar la tarjeta' : 'Reservar y pagar'} icono="check" ocupado={ocupado} ocupadoTexto="Reservando…" onClick={() => { void reservar(); }} />
                    <LegalCaptcha />
                </section>
            </Marco>
        );
    }

    return (
        <Marco negocio={negocio} linea={linea}>
            <section className="rv-pc-card" aria-labelledby="rv-rp-titulo">
                <div className="rv-pc-card__cab">
                    <h1 id="rv-rp-titulo">{estancia ? `Reserva tu ${unidad}` : `Reserva tu ${unidad}`}</h1>
                    <span>{estancia ? 'Elige el alojamiento, cuántos sois, la entrada y las noches.' : 'Elige dónde, cuántos sois y cuándo. Te confirmamos al momento.'}</span>
                </div>
                {aviso !== null && <AvisoPc texto={aviso.texto} tipo={aviso.tipo} />}
                {catalogo.zones.length === 0 ? (
                    <p className="rv-rp-nota">Ahora no hay nada que reservar por aquí.</p>
                ) : (
                    <>
                        <div role="radiogroup" aria-label="Dónde" className="rv-rp-zonas">
                            {catalogo.zones.map((z) => (
                                <button
                                    key={z.zone_id}
                                    type="button"
                                    role="radio"
                                    aria-checked={z.zone_id === e.zona}
                                    className="rv-rp-zona"
                                    onClick={() => { setE({ ...e, zona: z.zone_id, recurso: '', hora: null, turno: null, evento: null, personas: Math.min(Math.max(e.personas, z.min_party), z.max_party), noches: Math.max(z.min_nights ?? 1, 1) }); }}
                                >
                                    <strong>{z.public_name}</strong>
                                    <small>{[z.price_from === undefined ? '' : `Desde ${dinero(z.price_from)}`, `${String(z.min_party)} a ${String(z.max_party)} personas`].filter((x) => x !== '').join(' • ')}</small>
                                    <Icono nombre={z.zone_id === e.zona ? 'circle-check' : 'circle'} />
                                </button>
                            ))}
                        </div>
                        {zona !== undefined && (zona.selectable_resources ?? []).length > 0 && (
                            <Desplegable
                                etiqueta={`${unidad.slice(0, 1).toUpperCase()}${unidad.slice(1)} concreto (opcional)`}
                                valor={e.recurso}
                                opciones={[{ valor: '', texto: 'Cualquiera' }, ...(zona.selectable_resources ?? []).map((r) => ({ valor: r.resource_id ?? '', texto: r.public_name ?? '' }))]}
                                onChange={(v) => { setE({ ...e, recurso: v, hora: null, turno: null }); }}
                                tema={negocio.tema}
                            />
                        )}
                        {zona !== undefined && <Contador etiqueta="Personas" ayuda={`De ${String(zona.min_party)} a ${String(zona.max_party)}`} valor={e.personas} min={zona.min_party} max={zona.max_party} onCambio={(n) => { setE({ ...e, personas: n, hora: null, turno: null }); }} tema={negocio.tema} />}
                        {estancia && horasEstancia(zona) !== '' && <p className="rv-rp-nota rv-rp-nota--horas"><Icono nombre="clock" /><span>{horasEstancia(zona)}</span></p>}
                        <Fecha etiqueta={estancia ? 'Entrada' : 'Día'} valor={e.dia} min={hoy} {...(maxDia === undefined ? {} : { max: maxDia })} onChange={(v) => { setE({ ...e, dia: v, hora: null, turno: null, evento: null }); }} tema={negocio.tema} />
                        {estancia && <Contador etiqueta="Noches" ayuda={`De ${String(zona.min_nights ?? 1)} a ${String(zona.max_nights ?? 30)}`} valor={e.noches} min={zona.min_nights ?? 1} max={zona.max_nights ?? 30} onCambio={(n) => { setE({ ...e, noches: n }); }} tema={negocio.tema} />}
                        {!estancia && (
                            <div className="rv-rp-horas" role="radiogroup" aria-label="Hora">
                                {actuales === undefined ? <p className="rv-rp-nota">Buscando horas libres…</p> : actuales === null ? <p className="rv-rp-nota">No hemos podido buscar horas. Prueba de nuevo.</p> : actuales.slots.length === 0 ? (
                                    <p className="rv-rp-nota">{actuales.unavailable_reason === 'outside_window' ? 'Ese día está fuera del plazo para reservar.' : actuales.unavailable_reason === 'closed' ? 'Ese día está cerrado.' : 'No quedan horas libres ese día. Prueba con otro.'}</p>
                                ) : actuales.slots.map((s) => {
                                    const texto = s.shift_id !== undefined ? (zona?.shifts?.find((t) => t.shift_id === s.shift_id)?.name ?? s.local_time ?? '') : s.local_time ?? '';
                                    const elegida = s.shift_id !== undefined ? e.turno === s.shift_id : e.hora === s.local_time;
                                    return (
                                        <button key={`${s.starts_at}${s.shift_id ?? ''}`} type="button" role="radio" aria-checked={elegida} className="rv-rp-hora" onClick={() => { setE({ ...e, hora: s.local_time ?? null, turno: s.shift_id ?? null, evento: null }); }}>
                                            {texto}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {estancia && actuales !== undefined && (actuales === null || actuales.slots.length === 0) && <p className="rv-rp-nota">No hay alojamiento libre para todas esas noches. Prueba con otra entrada.</p>}
                        {eventoFijo === null && eventos.length > 1 && (
                            <Desplegable etiqueta="Evento" valor={e.evento ?? ''} opciones={[{ valor: '', texto: 'Elige el evento' }, ...eventos.map((x) => ({ valor: x.event_id, texto: x.public_name ?? '' }))]} onChange={(v) => { setE({ ...e, evento: v === '' ? null : v }); }} tema={negocio.tema} />
                        )}
                        <BotonPc texto="Seguir" icono="arrow-right" desactivado={!listoParaSeguir} ocupado={ocupado} onClick={seguir} />
                    </>
                )}
            </section>
        </Marco>
    );
}
